import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { captureError } from '@/lib/sentry'
import { useAuth } from '@/contexts/AuthContext'

export type TokenStatus = 'active' | 'graduated' | 'dead' | 'flagged'

// Fixed total supply used by the Meteora DBC launchpad for every dextoro coin.
// This is a launchpad-wide constant, not a per-coin value, so it's safe to
// hardcode. If the launchpad ever starts minting with variable supply, this
// will need to move into launched_tokens or a token_metadata table.
const DEXTORO_TOKEN_SUPPLY = 1_000_000_000

export interface ManagedToken {
  mintAddress: string
  name: string
  symbol: string
  creatorAddress: string
  creatorUsername: string | null
  status: TokenStatus
  isMigrated: boolean
  isVerified: boolean
  isTrending: boolean
  migratedAt: string | null
  createdAt: string
  volumeTotal: number
  volume24h: number
  previousVolume24h: number
  tradeCountTotal: number
  holderCount: number
  lastTradeAt: string | null
  /**
   * ATH market cap in USD, derived as `launched_tokens.ath_usd × 1B` where
   * `ath_usd` is the all-time high price per token. Null when the token has
   * never been priced. NOT a live market cap.
   */
  athMarketCapUsd: number | null
  isFlagged: boolean
  flagReason: string | null
}

interface TokenManagementResult {
  tokens: ManagedToken[]
  totalCount: number
}

export function useTokenManagement() {
  return useQuery({
    queryKey: ['token-management'],
    queryFn: async (): Promise<TokenManagementResult> => {
      // Parallel fetch: tokens + stats + profiles + flags
      const [tokensResult, statsResult, profilesResult, flagsResult] = await Promise.all([
        supabase
          .from('launched_tokens')
          .select('mint_address, name, symbol, creator_address, is_migrated, migrated_at, created_at, pool_address, ath_usd, is_verified, is_trending')
          .order('created_at', { ascending: false }),
        supabase
          .from('token_stats')
          .select('token_mint, volume_total, volume_24h, previous_volume_24h, trade_count_total, holder_count, last_trade_at'),
        supabase
          .from('creator_profiles')
          .select('wallet_address, username'),
        supabase
          .from('admin_token_flags')
          .select('token_mint, reason'),
      ])

      if (tokensResult.error) {
        throw tokensResult.error
      }

      // Build lookup maps
      const statsMap = new Map<string, {
        volumeTotal: number
        volume24h: number
        previousVolume24h: number
        tradeCountTotal: number
        holderCount: number
        lastTradeAt: string | null
      }>()
      statsResult.data?.forEach((s: Record<string, unknown>) => {
        statsMap.set(s.token_mint as string, {
          volumeTotal: Number(s.volume_total || 0),
          volume24h: Number(s.volume_24h || 0),
          previousVolume24h: Number(s.previous_volume_24h || 0),
          tradeCountTotal: Number(s.trade_count_total || 0),
          holderCount: Number(s.holder_count || 0),
          lastTradeAt: (s.last_trade_at as string) || null,
        })
      })

      const profileMap = new Map<string, string>()
      profilesResult.data?.forEach((p: Record<string, unknown>) => {
        if (p.username) profileMap.set(p.wallet_address as string, p.username as string)
      })

      const flagMap = new Map<string, string>()
      flagsResult.data?.forEach((f: Record<string, unknown>) => {
        flagMap.set(f.token_mint as string, (f.reason as string) || '')
      })

      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)

      const tokens: ManagedToken[] = (tokensResult.data || []).map((token: Record<string, unknown>) => {
        const mintAddress = token.mint_address as string
        const stats = statsMap.get(mintAddress)
        const isFlagged = flagMap.has(mintAddress)
        const isMigrated = token.is_migrated as boolean

        // Derive status
        let status: TokenStatus = 'active'
        if (isFlagged) {
          status = 'flagged'
        } else if (isMigrated) {
          status = 'graduated'
        } else if (stats) {
          const lastTrade = stats.lastTradeAt ? new Date(stats.lastTradeAt) : null
          const isDead = (!lastTrade || lastTrade < sevenDaysAgo) && stats.volumeTotal < 1
          if (isDead) status = 'dead'
        }

        const athUsdRaw = token.ath_usd
        const athUsd = athUsdRaw !== null && athUsdRaw !== undefined ? Number(athUsdRaw) : null
        const athMarketCapUsd = athUsd !== null && Number.isFinite(athUsd)
          ? athUsd * DEXTORO_TOKEN_SUPPLY
          : null

        return {
          mintAddress,
          name: token.name as string,
          symbol: token.symbol as string,
          creatorAddress: token.creator_address as string,
          creatorUsername: profileMap.get(token.creator_address as string) || null,
          status,
          isMigrated,
          isVerified: Boolean(token.is_verified),
          isTrending: Boolean(token.is_trending),
          migratedAt: (token.migrated_at as string) || null,
          createdAt: token.created_at as string,
          volumeTotal: stats?.volumeTotal || 0,
          volume24h: stats?.volume24h || 0,
          previousVolume24h: stats?.previousVolume24h || 0,
          tradeCountTotal: stats?.tradeCountTotal || 0,
          holderCount: stats?.holderCount || 0,
          lastTradeAt: stats?.lastTradeAt || null,
          athMarketCapUsd,
          isFlagged,
          flagReason: flagMap.get(mintAddress) || null,
        }
      })

      return { tokens, totalCount: tokens.length }
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
}

export function useFlagToken() {
  const queryClient = useQueryClient()
  const { adminUser } = useAuth()

  return useMutation({
    mutationFn: async ({ tokenMint, reason }: { tokenMint: string; reason: string }) => {
      if (!adminUser) throw new Error('Not authenticated')
      const { error } = await supabase
        .from('admin_token_flags')
        .insert({ token_mint: tokenMint, flagged_by: adminUser.id, reason })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['token-management'] })
    },
    onError: (error) => {
      captureError(error instanceof Error ? error : new Error('Flag token failed'), {
        feature: 'admin_dashboard',
        component: 'useFlagToken',
      })
    },
  })
}

export function useUnflagToken() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (tokenMint: string) => {
      const { error } = await supabase
        .from('admin_token_flags')
        .delete()
        .eq('token_mint', tokenMint)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['token-management'] })
    },
    onError: (error) => {
      captureError(error instanceof Error ? error : new Error('Unflag token failed'), {
        feature: 'admin_dashboard',
        component: 'useUnflagToken',
      })
    },
  })
}
